/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { isRegisteredUserLoggedIn } from 'sarsaparilla/auth';
import type { AuthScreen } from '../../authModal.slice';

type Parsed = {
    isValid: boolean;
    errorText: string | null;
    formattedValue: string | null;
};

export function matchPasswordValidator(passwordToMatch: string) {
    return (value: string) => {
        const parsed: Parsed = {
            isValid: false,
            errorText: null,
            formattedValue: null,
        };

        if (passwordToMatch !== value) {
            parsed.errorText = 'Passwords do not match';
            return parsed;
        }

        parsed.isValid = true;
        parsed.formattedValue = value;
        return parsed;
    };
}

export function isAuthScreen(screen: unknown): screen is AuthScreen {
    return (
        screen === 'login' ||
        screen === 'createAccount' ||
        screen === 'requestPasswordReset' ||
        screen === 'mfaCode'
    );
}

/**
 * Sanitize the redirect URL to prevent redirecting to a malicious site
 */
function sanitizeUrl(url?: string | null) {
    if (!url || typeof url !== 'string') {
        return null;
    }

    // If the URL points to a different domain, then we ignore for security
    if (
        url.includes('//') ||
        url.toLowerCase().includes('http') ||
        url.toLowerCase().includes('java') ||
        url.toLowerCase().includes('\\n') ||
        url.toLowerCase().includes('\\t')
    ) {
        return null;
    }

    return url;
}

export function maybePerformRedirect() {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    const redirectParam = params.get('redirect');

    if (!redirectParam) {
        return;
    }

    const sanitizedUrl = sanitizeUrl(redirectParam);
    params.delete('redirect');

    // Reconstruct the current url without the redirect search param, but keep the rest
    const newCurrentURL = `${currentUrl.pathname}${params.size ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', newCurrentURL);

    if (sanitizedUrl && isRegisteredUserLoggedIn()) {
        window.location.pathname = sanitizedUrl;
    }
}
